import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useToastRefusal, vocab } from '@/ds';
import { useOfferUndo } from '@/app/undo/offer-undo';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { ApiError, isApiError, reasonOf } from '@/lib/api/errors';
import { fileNameOf, saveFile } from '@/lib/download/download';
import { auditKeys } from '@/features/audit/keys';
import { eventKeys } from '@/features/events/api';
import { candidateKeys } from './keys';
import { candidatesSearchSchema, toApiQuery, toExportQuery, type CandidatesSearch } from './search';
import { statusToApi, type CandidateTab, type Rejection } from './vocab';

export type CandidateListItem = Schemas['StagedEventListItemDto'];
export type CandidatePage = Schemas['StagedEventPageResponse'];
export type Candidate = Schemas['StagedEventDetailDto'];
export type CandidateUpdate = Schemas['UpdateStagedEventRequest'];

export const candidatesQuery = (search: CandidatesSearch) =>
  queryOptions({
    queryKey: candidateKeys.list(search),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/staged-events', { params: { query: toApiQuery(search) } })),
    placeholderData: keepPreviousData,
  });

const FULL_PAGE_SIZE = 100;
/** How many pages of pending records the Inbox reads; past them it says how many it left out. */
const PENDING_PAGES = 3;

/** Pending candidates, newest first, read in full up to `PENDING_PAGES` pages. */
export const pendingCandidatesQuery = () =>
  queryOptions({
    queryKey: candidateKeys.pending(),
    queryFn: async () => {
      const items: CandidateListItem[] = [];
      let totalCount = 0;
      for (let pageNumber = 1; pageNumber <= PENDING_PAGES; pageNumber++) {
        const page = await unwrap(
          api.GET('/api/scraping/staged-events', {
            params: {
              query: { status: statusToApi('pending'), pageNumber, pageSize: FULL_PAGE_SIZE },
            },
          }),
        );
        items.push(...page.items);
        totalCount = page.totalCount;
        if (items.length >= totalCount || page.items.length === 0) break;
      }
      return { items, totalCount };
    },
  });

/**
 * Decided records whose page the pipeline can't find any more, newest first: approved ones and
 * rejected ones whose event stays (a kept published version), one page of each.
 */
export const flaggedDecidedQuery = () =>
  queryOptions({
    queryKey: candidateKeys.flagged(),
    queryFn: async () => {
      const read = (status: CandidateTab) =>
        unwrap(
          api.GET('/api/scraping/staged-events', {
            params: {
              query: {
                status: statusToApi(status),
                possiblyCancelled: true,
                pageNumber: 1,
                pageSize: FULL_PAGE_SIZE,
              },
            },
          }),
        );
      const [approved, rejected] = await Promise.all([read('published'), read('rejected')]);
      return {
        items: [...approved.items, ...rejected.items],
        totalCount: approved.totalCount + rejected.totalCount,
      };
    },
  });

/**
 * The candidates linked to each of the given published events: the list's scope (all but expired)
 * and the expired ones, read in full.
 */
export const linkedCandidatesQuery = (eventIds: string[]) =>
  queryOptions({
    queryKey: candidateKeys.linked(eventIds),
    queryFn: async () => {
      const read = async (status?: string) => {
        const items: CandidateListItem[] = [];
        for (let pageNumber = 1; ; pageNumber++) {
          const page = await unwrap(
            api.GET('/api/scraping/staged-events', {
              params: {
                query: {
                  linkedRaceEventId: eventIds,
                  status,
                  pageNumber,
                  pageSize: FULL_PAGE_SIZE,
                },
              },
            }),
          );
          items.push(...page.items);
          if (items.length >= page.totalCount || page.items.length === 0) return items;
        }
      };
      const [current, expired] = await Promise.all([read(), read(statusToApi('expired'))]);
      const byEvent = new Map<string, CandidateListItem[]>();
      for (const item of [...current, ...expired]) {
        if (!item.linkedRaceEventId) continue;
        byEvent.set(item.linkedRaceEventId, [...(byEvent.get(item.linkedRaceEventId) ?? []), item]);
      }
      return byEvent;
    },
    enabled: eventIds.length > 0,
  });

export const candidateStatsQuery = () =>
  queryOptions({
    queryKey: candidateKeys.stats(),
    queryFn: () => unwrap(api.GET('/api/scraping/staged-events/stats')),
  });

export const candidateQuery = (id: string) =>
  queryOptions({
    queryKey: candidateKeys.detail(id),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/staged-events/{id}', { params: { path: { id } } })),
  });

/** The key changes when the record moves to another capture, so a re-read's newer one is fetched fresh. */
export const candidateSnapshotQuery = (id: string, snapshotId: string) =>
  queryOptions({
    queryKey: candidateKeys.snapshot(id, snapshotId),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/staged-events/{id}/snapshot', { params: { path: { id } } })),
    // A captured page never changes.
    staleTime: Infinity,
  });

export const linkCandidatesQuery = (id: string) =>
  queryOptions({
    queryKey: candidateKeys.linkCandidates(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/staged-events/{id}/link-candidates', { params: { path: { id } } }),
      ),
  });

export type CandidatePatch = Partial<Omit<CandidateUpdate, 'version'>>;

/**
 * The request body for an edit: the record as it stands, with the changed fields applied. The
 * version makes the API refuse the edit if the record changed since it was read.
 */
export function toUpdate(candidate: Candidate, patch: CandidatePatch): CandidateUpdate {
  const { summary } = candidate;
  return {
    version: candidate.version,
    name: summary.name,
    city: summary.city,
    country: summary.country,
    date: summary.date,
    startTimeLocal: summary.startTimeLocal,
    descriptionEn: candidate.descriptionEn,
    latitude: candidate.latitude,
    longitude: candidate.longitude,
    imageId: candidate.imageId,
    races: candidate.races.map((race) => ({
      name: race.name,
      category: race.category,
      distanceKm: race.distanceKm,
      altitudeChangeM: race.altitudeChangeM,
      startTimeLocal: race.startTimeLocal,
    })),
    ...patch,
  };
}

function useInvalidateCandidates() {
  const queryClient = useQueryClient();
  return (id: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) }),
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: candidateKeys.stats() }),
      queryClient.invalidateQueries({ queryKey: candidateKeys.linkedAll() }),
      queryClient.invalidateQueries({ queryKey: eventKeys.all }),
    ]);
}

// One queue per record: a decision waits for the edits before it, so it publishes what was saved.
const recordScope = (id: string) => ({ id: `candidate:${id}` });

/**
 * Edits run against the record as the previous edit left it, so a quick second edit neither
 * reverts the first nor trips the version check.
 */
export function useUpdateCandidate(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCandidates();
  return useMutation({
    scope: recordScope(id),
    mutationFn: async (patch: CandidatePatch) => {
      const current = await queryClient.ensureQueryData(candidateQuery(id));
      return unwrap(
        api.PUT('/api/scraping/staged-events/{id}', {
          params: { path: { id } },
          body: toUpdate(current, patch),
        }),
      );
    },
    onSettled: () => invalidate(id),
  });
}

export function usePublishCandidate(id: string) {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    scope: recordScope(id),
    mutationFn: () =>
      unwrap(api.POST('/api/scraping/staged-events/{id}/approve', { params: { path: { id } } })),
    // Not awaited: the page moves on to the next record without waiting for the refetch.
    onSettled: () => void invalidate(id),
  });
}

export function useRejectCandidate(id: string) {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    scope: recordScope(id),
    mutationFn: (rejection: Rejection) =>
      unwrap(
        api.POST('/api/scraping/staged-events/{id}/reject', {
          params: { path: { id } },
          body: rejection,
        }),
      ),
    onSettled: () => void invalidate(id),
  });
}

/**
 * "Link to this event" is one decision: approve with `linkTo` sets the target and applies the
 * record to that event instead of creating one. If the approval is refused, the link stays.
 */
export function useLinkAndPublishCandidate(id: string) {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    scope: recordScope(id),
    mutationFn: (targetRaceEventId: string) =>
      unwrap(
        api.POST('/api/scraping/staged-events/{id}/approve', {
          params: { path: { id } },
          body: { linkTo: targetRaceEventId },
        }),
      ),
    onSettled: () => void invalidate(id),
  });
}

interface UnlinkHandlers {
  onUnlinked: () => void;
  onRefused: (refusal: { title: string; reason?: string }) => void;
}

/**
 * Clears a pending record's link. The toast's Undo puts it back, with the origin it had. The toast
 * and a refusal outlive the page (it remounts per record); while the page is up, a refusal goes to
 * `onRefused` as its Banner, and once it is gone, to the toast layer.
 */
export function useUnlinkWithUndo(id: string, { onUnlinked, onRefused }: UnlinkHandlers) {
  const invalidate = useInvalidateCandidates();
  const offerUndo = useOfferUndo();
  const refuse = useToastRefusal();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const mutation = useMutation({
    scope: recordScope(id),
    mutationFn: (_: { record: string; event: string | null }) =>
      unwrap(api.POST('/api/scraping/staged-events/{id}/unlink', { params: { path: { id } } })),
    onSuccess: (recorded, { record, event }) => {
      const named = event === null ? vocab.UNLINK.gone : `“${event}”`;
      offerUndo(`${vocab.UNLINK.done} ${named}. ${vocab.UNLINK.consequence}`, {
        entries: [recorded.auditEntryId],
        undone: `${vocab.UNLINK.relinked} “${record}” to ${named}.`,
        refused: `${vocab.UNLINK.undoRefused} “${record}”`,
      });
      return invalidate(id);
    },
    onError: (error, { record }) => {
      if (!mounted.current) {
        refuse({ title: `${vocab.UNLINK.refused} “${record}”`, reason: reasonOf(error) });
      }
    },
  });
  return {
    pending: mutation.isPending,
    unlink: (record: string, event: string | null) =>
      mutation.mutate(
        { record, event },
        {
          onSuccess: onUnlinked,
          onError: (error) =>
            onRefused({ title: `${vocab.UNLINK.refused} “${record}”`, reason: reasonOf(error) }),
        },
      ),
  };
}

/** Keeps a flagged published event as it is; the flag stays off until its page is seen and then missed again. */
export function useStillOn(id: string) {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    scope: recordScope(id),
    mutationFn: () =>
      unwrap(api.POST('/api/scraping/staged-events/{id}/still-on', { params: { path: { id } } })),
    onSettled: () => void invalidate(id),
  });
}

/** Asks for the record's page to be read again on its source's next run. */
export function useRequestReRead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    scope: recordScope(id),
    mutationFn: () =>
      unwrap(api.POST('/api/scraping/staged-events/{id}/re-read', { params: { path: { id } } })),
    // Awaited, so the page swaps its row once the record carries the request.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: auditKeys.all }),
      ]),
  });
}

/** Takes the event this record published out of the app, for good; the record returns to review with the reason. */
export function useRemoveFromApp(id: string) {
  const invalidate = useInvalidateCandidates();
  return useMutation({
    scope: recordScope(id),
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/api/scraping/staged-events/{id}/unpublish', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSettled: () => void invalidate(id),
  });
}

export type BulkDecision = { kind: 'publish' } | { kind: 'reject'; rejection: Rejection };

export interface BulkOutcome {
  done: string[];
  /** The audit entry each decision recorded, by record, for Undo. */
  entries: Map<string, string>;
  refused: { id: string; reason: string }[];
  /** The run stopped early (offline, or the rate limit): these weren't tried. */
  stopped?: { reason: string; ids: string[] };
}

const stopsTheRun = (error: unknown) =>
  isApiError(error) && (error.status === 0 || error.status === 429);

/** One request per record, in turn, so each refusal keeps its reason. Offline or a 429 stops the run. */
export function useBulkDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      decision,
      onProgress,
    }: {
      ids: string[];
      decision: BulkDecision;
      onProgress?: (settled: number) => void;
    }): Promise<BulkOutcome> => {
      const outcome: BulkOutcome = { done: [], entries: new Map(), refused: [] };
      for (const [index, id] of ids.entries()) {
        const params = { path: { id } };
        try {
          const recorded = await unwrap(
            decision.kind === 'publish'
              ? api.POST('/api/scraping/staged-events/{id}/approve', { params })
              : api.POST('/api/scraping/staged-events/{id}/reject', {
                  params,
                  body: decision.rejection,
                }),
          );
          outcome.done.push(id);
          if (recorded.auditEntryId) outcome.entries.set(id, recorded.auditEntryId);
        } catch (error) {
          const reason = reasonOf(error) ?? 'The server gave no reason.';
          if (stopsTheRun(error)) {
            outcome.stopped = { reason, ids: ids.slice(index) };
            break;
          }
          outcome.refused.push({ id, reason });
        }
        onProgress?.(index + 1);
      }
      return outcome;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: candidateKeys.all }),
        queryClient.invalidateQueries({ queryKey: eventKeys.all }),
      ]),
  });
}

/** Every candidate with a status, from one source or all of them, as the export takes it. */
export const exportScope = (status: CandidateTab, source?: string): CandidatesSearch =>
  candidatesSearchSchema.parse({ status, source });

/** Downloads the transfer file for the list's filters, or for the given records only. */
export function useExportCandidates() {
  return useMutation({
    mutationFn: async ({ search, ids }: { search: CandidatesSearch; ids?: string[] }) => {
      let result;
      try {
        result = await api.GET('/api/scraping/staged-events/export', {
          params: { query: toExportQuery(search, ids) },
          parseAs: 'blob',
        });
      } catch (cause) {
        throw ApiError.network(cause);
      }
      if (!result.response.ok) throw new ApiError(result.response.status, result.error as never);
      const blob = result.data as Blob;
      saveFile(blob, fileNameOf(result.response) ?? 'staged-events.json');
      return blob;
    },
  });
}
